/**
 * App create form (game-mgmt-refactor #7).
 *
 * Replaces the 4-step AppWizard with a single form. The admin enters a name,
 * description, category, a full Docker image reference with a mutable tag
 * (e.g. `harbor.rehapiano.com/team/app:test-latest`), the compatible device
 * types, and may optionally upload a `.yaml` config (stored as-is). Versioning
 * and prod-promotion are gone — git is the source of version truth.
 */
import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { appsApi, AppCreate, appTeamsApi, deviceTypesApi } from '../services/api'
import { useAuth } from '../auth/AuthProvider'
import { isValidImageRef, branchFromTag } from '../utils/imageRef'

const CATEGORIES = ['diagnostics', 'rehabilitation', 'cognitive', 'other'] as const

const AppForm: React.FC = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { hasRole } = useAuth()
  const isAdmin = hasRole('system_admin') || hasRole('app_admin')

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState<string>('rehabilitation')
  const [imageRef, setImageRef] = useState('')
  const [deviceTypeIds, setDeviceTypeIds] = useState<string[]>([])
  const [teamId, setTeamId] = useState('')
  const [configYaml, setConfigYaml] = useState<string | undefined>(undefined)
  const [configName, setConfigName] = useState<string>('')
  const [submitError, setSubmitError] = useState<string | null>(null)

  const { data: teams } = useQuery({
    queryKey: ['app-teams', isAdmin ? 'all' : 'mine'],
    queryFn: () => (isAdmin ? appTeamsApi.list() : appTeamsApi.getMyTeams()),
  })
  const { data: deviceTypes } = useQuery({
    queryKey: ['device-types'],
    queryFn: () => deviceTypesApi.list(),
  })

  const teamList = teams?.data ?? []
  const activeDeviceTypes = useMemo(
    () => (deviceTypes?.data ?? []).filter((t) => t.is_active),
    [deviceTypes],
  )

  const effectiveTeamId = teamId || (teamList.length === 1 ? teamList[0].id : '')
  const imageValid = isValidImageRef(imageRef)
  const branchHint = branchFromTag(imageRef)

  const canSubmit =
    name.trim().length > 0 &&
    !!category &&
    imageValid &&
    deviceTypeIds.length > 0 &&
    !!effectiveTeamId

  const createMutation = useMutation({
    mutationFn: (payload: AppCreate) => appsApi.create(payload),
    onSuccess: (resp) => {
      queryClient.invalidateQueries({ queryKey: ['apps'] })
      navigate(`/apps/${resp.data.id}`)
    },
    onError: (err: unknown) => {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setSubmitError(typeof detail === 'string' ? detail : 'Failed to create app')
    },
  })

  const toggleDeviceType = (id: string) =>
    setDeviceTypeIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    )

  const onConfigFile = async (file?: File) => {
    if (!file) {
      setConfigYaml(undefined)
      setConfigName('')
      return
    }
    const text = await file.text()
    setConfigYaml(text)
    setConfigName(file.name)
  }

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitError(null)
    if (!canSubmit) return
    const payload: AppCreate = {
      name: name.trim(),
      description: description.trim() || undefined,
      category,
      docker_image_url: imageRef.trim(),
      app_team_id: effectiveTeamId,
      supported_device_type_ids: deviceTypeIds,
      config_yaml: configYaml,
    }
    createMutation.mutate(payload)
  }

  return (
    <div className="max-w-2xl mx-auto p-6">
      <h1 className="text-2xl font-semibold mb-1">New App</h1>
      <p className="text-gray-500 mb-6">
        Apps are versioned in git. Enter the image tag (e.g. <code>:test-latest</code>);
        it will require approval before reaching devices.
      </p>

      <form onSubmit={onSubmit} className="space-y-5" aria-label="create-app-form">
        <div>
          <label className="block text-sm font-medium mb-1" htmlFor="app-name">Name *</label>
          <input
            id="app-name"
            className="w-full border rounded px-3 py-2"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1" htmlFor="app-desc">Description</label>
          <textarea
            id="app-desc"
            className="w-full border rounded px-3 py-2"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1" htmlFor="app-category">Category *</label>
          <select
            id="app-category"
            className="w-full border rounded px-3 py-2"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>

        {teamList.length > 1 && (
          <div>
            <label className="block text-sm font-medium mb-1" htmlFor="app-team">Team *</label>
            <select
              id="app-team"
              className="w-full border rounded px-3 py-2"
              value={effectiveTeamId}
              onChange={(e) => setTeamId(e.target.value)}
            >
              <option value="">— select team —</option>
              {teamList.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium mb-1" htmlFor="app-image">
            Docker image (image:tag) *
          </label>
          <input
            id="app-image"
            className={`w-full border rounded px-3 py-2 font-mono text-sm ${
              imageRef && !imageValid ? 'border-red-500' : ''
            }`}
            placeholder="harbor.rehapiano.com/team/app:test-latest"
            value={imageRef}
            onChange={(e) => setImageRef(e.target.value)}
            aria-invalid={!!imageRef && !imageValid}
          />
          {imageRef && !imageValid && (
            <p className="text-red-600 text-sm mt-1" role="alert">
              Expected host/project/name:tag or host/project/name@sha256:&lt;digest&gt;
            </p>
          )}
          {branchHint && (
            <p className="text-teal-700 text-sm mt-1">Branch: {branchHint}</p>
          )}
        </div>

        <div>
          <span className="block text-sm font-medium mb-1">Device types *</span>
          <div className="space-y-1">
            {activeDeviceTypes.map((dt) => (
              <label key={dt.id} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={deviceTypeIds.includes(dt.id)}
                  onChange={() => toggleDeviceType(dt.id)}
                />
                <span>{dt.name}</span>
              </label>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1" htmlFor="app-config">
            Config file (.yaml, optional)
          </label>
          <input
            id="app-config"
            type="file"
            accept=".yaml,.yml"
            onChange={(e) => onConfigFile(e.target.files?.[0])}
          />
          {configName && <p className="text-gray-600 text-sm mt-1">Selected: {configName}</p>}
        </div>

        {submitError && (
          <p className="text-red-600 text-sm" role="alert">{submitError}</p>
        )}

        <div className="flex gap-3">
          <button
            type="submit"
            className="bg-blue-600 text-white rounded px-4 py-2 disabled:bg-gray-400"
            disabled={!canSubmit || createMutation.isPending}
          >
            {createMutation.isPending ? 'Creating…' : 'Create app'}
          </button>
          <button
            type="button"
            className="border rounded px-4 py-2"
            onClick={() => navigate('/apps')}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  )
}

export default AppForm
